import { Trash2 } from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import type {AffectedBundle} from "@/components/bundle/bundleTypes";
import { Checkbox } from '@/components/ui/checkbox';
import { useMatterJurisdiction } from "@/components/contexts/MatterJurisdictionContext";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/NewButton';
import { deleteFolder, type Folder } from '@/hooks/useFolders';
import { apiErrorMessage } from '@/lib/api-client';

interface DeleteFolderDialogProps {
  matterSlug: string;
  folder?: Folder;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const DeleteFolderDialog: React.FC<DeleteFolderDialogProps> = ({
  matterSlug,
  folder,
  isOpen,
  onClose,
  onSuccess
}) => {
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const [isLoading, setIsLoading] = useState(false);
  const [affectedBundles, setAffectedBundles] = useState<AffectedBundle[]>([]);
  const [deleteAttachments, setDeleteAttachments] = useState(true);
  const deleteInFlightRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setDeleteAttachments(true);
    }
  }, [isOpen]);

  const getErrorMessage = (error: any): string => {
    return apiErrorMessage(error) || 'Failed to delete folder';
  };

  const handleClose = () => {
    setAffectedBundles([]);
    onClose();
  };

  const handleDelete = async (force = false) => {
    if (!folder || deleteInFlightRef.current) return;

    deleteInFlightRef.current = true;
    setIsLoading(true);
    try {
      await deleteFolder(matterSlug, folder.guid, true, force, deleteAttachments);
      toast.success('Folder and all contents deleted successfully');
      setAffectedBundles([]);
      onSuccess();
      onClose();
    } catch (error: any) {
      const bundleWarning = error?.response?.status === 409 &&
        error?.response?.data?.detail?.warnings?.find((w: any) => w.type === 'bundle_references');
      if (bundleWarning) {
        setAffectedBundles(bundleWarning.affected_bundles);
        return;
      }
      console.error('Error deleting folder:', error);
      toast.error(getErrorMessage(error));
    } finally {
      deleteInFlightRef.current = false;
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-500" />
            Delete Folder
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-start">
            <div className="text-sm">
              Are you sure you want to delete the folder &apos;<strong>{folder?.name}</strong>&apos;? This cannot be undone.
            </div>
          </div>
          <div className="flex items-start gap-2 rounded-md border border-stone-200 bg-stone-50 p-3 text-sm">
            <Checkbox
              id={`delete-folder-attachments-${folder?.guid}`}
              checked={deleteAttachments}
              onCheckedChange={(checked) => setDeleteAttachments(checked === true)}
              disabled={isLoading}
            />
            <label htmlFor={`delete-folder-attachments-${folder?.guid}`} className="leading-5 cursor-pointer">
              <span className="font-medium text-stone-900">Also delete attachments from emails being deleted</span>
              <span className="block text-stone-600">
                This applies only to attachments stored outside this folder. Everything inside this folder and its subfolders will be deleted either way.
              </span>
            </label>
          </div>
          {affectedBundles.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <p className="font-medium mb-1">This folder contains files used in the following {affectedBundles.length > 1 ? ui_labels.bundle_plural.toLowerCase() : ui_labels.bundle_singular.toLowerCase()}:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                {affectedBundles.map(b => (
                  <li key={b.bundle_id}>{b.bundle_title} ({b.affected_file_count} file{b.affected_file_count > 1 ? 's' : ''})</li>
                ))}
              </ul>
              <p className="mt-1">Deleting this folder will remove these files from {affectedBundles.length === 1 ? `this ${ui_labels.bundle_singular.toLowerCase()}` : `these ${ui_labels.bundle_plural.toLowerCase()}`}. The {affectedBundles.length > 1 ? ui_labels.bundle_plural.toLowerCase() : ui_labels.bundle_singular.toLowerCase()} may need to be regenerated.</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading} text='Cancel' />
          <Button
            variant="destructive"
            onClick={() => handleDelete(affectedBundles.length > 0)}
            disabled={isLoading}
            loading={isLoading}
            text={affectedBundles.length > 0 ? 'Delete Anyway' : 'Delete Folder'}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
